import { baseUrl } from './api.js';

/**
 * Krok AI3 bod 1 — Asistentka v aplikaci (chat/hlas). Volá REOS API přímo
 * (ne proxy na core, viz `asistentka-api.ts`) — `POST /v1/assistant/chat`
 * jen rozpozná jednu větu a připraví MCP zápis ke schválení
 * (`mcp_approvals`), nikdy nic nezapisuje přímo.
 */

export type AssistantChatResult =
  | { readonly status: 'unknown' }
  | { readonly status: 'prepared'; readonly approvalId: string; readonly tool: string; readonly args: Record<string, unknown> };

export async function sendAssistantChat(text: string): Promise<AssistantChatResult> {
  const res = await fetch(`${baseUrl}/v1/assistant/chat`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ text }),
  });
  if (!res.ok) throw new Error(`asistentka chat: ${res.status}`);
  return (await res.json()) as AssistantChatResult;
}

/**
 * Krok NAP5 bod A11 — diktat Asistentce. Nahravku z `MediaRecorder` posila
 * jako multipart na `POST /v1/assistant/transcribe` (server ji preda
 * rh-whisper tymze klientem jako diktat v naboru) a vraci jen TEXT, ktery se
 * vlozi do editoru. Nic se nezapisuje — zapis porad resi `sendAssistantChat`
 * a brana schvaleni.
 *
 * 503 = sluzba prepisu neni nasazena; volajici to pozna podle `notConfigured`
 * a ukaze hlasku serveru (uz je lokalizovana, `assistant.voice.not_configured`),
 * ne vlastni druhou vetu.
 */
export class AssistantTranscribeError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string | null,
  ) {
    super(`asistentka transcribe: ${status}`);
    this.name = 'AssistantTranscribeError';
  }

  get notConfigured(): boolean {
    return this.status === 503;
  }
}

export async function transcribeAssistantAudio(blob: Blob): Promise<string> {
  const form = new FormData();
  form.set('audio', blob, 'diktat.webm');
  const res = await fetch(`${baseUrl}/v1/assistant/transcribe`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  if (!res.ok) {
    const problem = (await res.json().catch(() => null)) as { title?: string } | null;
    throw new AssistantTranscribeError(res.status, problem?.title ?? null);
  }
  const body = (await res.json()) as { text: string };
  return body.text;
}
